import { Link } from "react-router-dom";
import { ArrowLeft, Building2, Users, Ticket, Cloud, Calendar, ChevronRight, Check, FileDown, LifeBuoy, Home } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";

/**
 * Property Managers — Sample Plans & Billing preview.
 * This is a design mockup for the future property-management billing dashboard.
 * No live subscriptions, invoices, or payment processing yet.
 * NOTE: intentionally does NOT show any VAT line on the billing summary.
 */

const plans = [
  {
    id: "starter",
    name: "Starter",
    tagline: "For small portfolios just getting started",
    price: "AED 29",
    unit: "/ unit / month",
    icon: Home,
    features: ["Up to 10 units", "Up to 5 staff", "Maintenance tickets", "Basic reports", "Email support"],
    cta: "Choose Starter",
  },
  {
    id: "professional",
    name: "Professional",
    tagline: "For growing property management teams",
    price: "AED 49",
    unit: "/ unit / month",
    icon: Building2,
    features: ["Unlimited units", "Unlimited staff", "Unlimited tickets", "Advanced analytics", "1 year cloud storage", "Priority support"],
    cta: "Current Plan",
    accent: true,
    current: true,
  },
  {
    id: "enterprise",
    name: "Enterprise",
    tagline: "For large enterprises & complex portfolios",
    price: "Custom",
    unit: "pricing",
    icon: Building2,
    features: ["Everything in Professional", "Dedicated account manager", "Custom integrations", "SLA guarantee", "API access", "24/7 priority support"],
    cta: "Contact Sales",
  },
];

const invoices = [
  { date: "20 Jun 2026", ref: "INV-2026-061", amount: "AED 980", status: "Paid" },
  { date: "20 May 2026", ref: "INV-2026-051", amount: "AED 980", status: "Paid" },
  { date: "20 Apr 2026", ref: "INV-2026-041", amount: "AED 980", status: "Paid" },
];

export default function PropertyBilling() {
  return (
    <div className="bg-bone min-h-screen">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-20">
        <Link
          to="/#property-managers"
          className="inline-flex items-center gap-2 text-sm text-forest hover:text-forest-hover mb-6"
          data-testid="back-to-pm"
        >
          <ArrowLeft size={16} /> Back to Property Managers
        </Link>

        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div>
            <div className="flex items-center gap-3">
              <p className="label-eyebrow">Property Managers</p>
              <span className="text-[10px] font-mono uppercase tracking-widest bg-white border border-[#E2DFD8] text-forest px-2 py-0.5 rounded-full">
                sample preview · not yet live
              </span>
            </div>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light tracking-tighter leading-[0.98] text-forest mt-3">
              Plans &amp; Billing
            </h1>
            <p className="mt-3 text-ink/65 max-w-xl">
              A preview of how portfolio-level plans and billing will appear inside Noa. Pricing shown per unit, per month. This is a design mockup — no live subscriptions or invoices yet.
            </p>
          </div>
        </div>

        {/* Current Plan card */}
        <div className="mt-8 rounded-3xl bg-forest text-bone p-8 md:p-10 relative overflow-hidden" data-testid="current-plan-card">
          <div className="absolute -right-16 -top-16 w-72 h-72 rounded-full bg-terracotta/10 blur-3xl" />
          <div className="relative grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
            <div className="md:col-span-7">
              <div className="flex items-center gap-3">
                <p className="label-eyebrow text-bone/60">Current plan</p>
                <span className="text-xs font-mono bg-terracotta text-bone rounded-full px-3 py-1">Active</span>
              </div>
              <p className="font-serif text-4xl md:text-5xl mt-3 font-light">Professional</p>
              <p className="text-bone/70 mt-2">For growing property management teams.</p>
              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                <MiniStat icon={Building2} label="Unlimited" sub="Buildings" />
                <MiniStat icon={Users} label="Unlimited" sub="Staff" />
                <MiniStat icon={Ticket} label="Unlimited" sub="Tickets" />
                <MiniStat icon={Cloud} label="1 Year" sub="Cloud Storage" />
              </div>
            </div>
            <div className="md:col-span-5 md:text-right">
              <p className="label-eyebrow text-bone/60">Price per unit</p>
              <p className="font-serif text-4xl mt-1">AED 49 <span className="text-sm text-bone/60">/ unit / month</span></p>
              <p className="mt-4 text-xs text-bone/60 flex items-center gap-2 md:justify-end">
                <Calendar size={12} /> Next billing on 20 July 2026
              </p>
            </div>
          </div>
        </div>

        {/* Plans grid */}
        <div className="mt-10">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <p className="label-eyebrow">Choose the right plan</p>
              <h2 className="font-serif text-3xl md:text-4xl text-forest font-light mt-1">for your property portfolio</h2>
            </div>
            <p className="text-xs text-ink/55">Pricing is per unit (flat, villa, or commercial space) per month.</p>
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="pm-plans-grid">
            {plans.map((p) => (
              <div
                key={p.id}
                className={`relative rounded-3xl p-8 border flex flex-col transition-all hover:-translate-y-1 hover:shadow-xl ${
                  p.accent ? "bg-forest text-bone border-forest" : "bg-white border-[#E2DFD8]"
                }`}
                data-testid={`pm-plan-${p.id}`}
              >
                {p.accent && (
                  <span className="absolute -top-3 left-8 bg-terracotta text-bone text-xs font-mono px-3 py-1 rounded-full">
                    most popular
                  </span>
                )}
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full grid place-items-center ${p.accent ? "bg-bone/10 text-bone" : "bg-forest/5 text-forest"}`}>
                    <p.icon size={18} strokeWidth={1.5} />
                  </div>
                  <p className={`font-serif text-2xl ${p.accent ? "text-bone" : "text-ink"}`}>{p.name}</p>
                </div>
                <p className={`mt-3 text-sm ${p.accent ? "text-bone/70" : "text-ink/60"}`}>{p.tagline}</p>
                <div className="mt-6 flex items-baseline gap-2 flex-wrap">
                  <span className="font-serif text-4xl font-light">{p.price}</span>
                  <span className={`text-sm ${p.accent ? "text-bone/60" : "text-ink/55"}`}>{p.unit}</span>
                </div>
                <ul className={`mt-6 space-y-2.5 text-sm flex-1`}>
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check size={16} strokeWidth={1.5} className={`mt-0.5 shrink-0 ${p.accent ? "text-terracotta" : "text-forest"}`} />
                      <span className={p.accent ? "text-bone/85" : "text-ink/75"}>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  disabled={p.current}
                  className={`mt-8 w-full rounded-full h-12 ${
                    p.accent
                      ? "bg-bone text-forest hover:bg-bone/90 disabled:opacity-100"
                      : "bg-white border border-forest text-forest hover:bg-forest hover:text-bone"
                  }`}
                  data-testid={`pm-plan-cta-${p.id}`}
                >
                  {p.cta}
                </Button>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-ink/55 bg-white border border-[#E2DFD8] rounded-xl px-4 py-3 inline-block">
            ℹ Pricing is per unit (flat, villa, or commercial space) per month.
          </p>
        </div>

        {/* Billing Overview + Payment + History */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Billing Overview (NO VAT LINE) */}
          <div className="surface-card p-8" data-testid="billing-overview">
            <p className="label-eyebrow mb-4">Billing overview</p>
            <dl className="space-y-3 text-sm">
              <Row label="Current plan" value="Professional" />
              <Row label="Units managed" value="20 Units" />
              <Row label="Price per unit" value="AED 49" />
              <div className="h-px bg-[#E2DFD8] my-4" />
              <Row label="Subtotal (20 Units)" value="AED 980" />
              <div className="h-px bg-[#E2DFD8] my-4" />
              <div className="flex items-center justify-between">
                <dt className="font-serif text-lg text-ink">Total amount</dt>
                <dd className="font-serif text-2xl text-forest">AED 980 <span className="text-xs text-ink/50">/ month</span></dd>
              </div>
            </dl>
            <Button variant="outline" className="mt-6 w-full rounded-full h-11 border-forest text-forest hover:bg-forest hover:text-bone" data-testid="download-invoice">
              <FileDown size={16} className="mr-2" /> Download invoice
            </Button>
            <p className="mt-3 text-[11px] text-ink/50">Taxes are not shown in this preview.</p>
          </div>

          {/* Payment Method + History */}
          <div className="flex flex-col gap-6">
            <div className="surface-card p-8" data-testid="payment-method">
              <p className="label-eyebrow mb-4">Payment method</p>
              <div className="flex items-center justify-between bg-bonewarm rounded-xl px-4 py-3">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs bg-white border border-[#E2DFD8] rounded-md px-2 py-1">VISA</span>
                  <div>
                    <p className="text-sm font-medium">Ending in 4242</p>
                    <p className="text-[11px] text-ink/50">Primary</p>
                  </div>
                </div>
                <ChevronRight size={16} className="text-ink/40" />
              </div>
              <Button variant="ghost" className="mt-3 w-full text-forest hover:bg-forest/5 rounded-full h-11">
                + Add payment method
              </Button>
            </div>

            <div className="surface-card p-8" data-testid="billing-history">
              <div className="flex items-center justify-between mb-4">
                <p className="label-eyebrow">Billing history</p>
                <button className="text-xs text-forest hover:text-forest-hover font-medium">View all</button>
              </div>
              <ul className="space-y-3">
                {invoices.map((inv) => (
                  <li key={inv.ref} className="flex items-center justify-between text-sm">
                    <div>
                      <p className="font-medium">{inv.date}</p>
                      <p className="text-[11px] text-ink/50 font-mono">Invoice #{inv.ref}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">{inv.amount}</p>
                      <p className="text-[11px] text-moss font-medium">{inv.status}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Help */}
        <div className="mt-10 surface-card p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-forest/5 text-forest grid place-items-center shrink-0">
              <LifeBuoy size={18} strokeWidth={1.5} />
            </div>
            <div>
              <p className="font-serif text-lg text-ink">Need help?</p>
              <p className="text-sm text-ink/60 mt-1">Our team is here for any billing or subscription queries.</p>
            </div>
          </div>
          <Link to="/store#enquiry">
            <Button className="rounded-full bg-forest hover:bg-forest-hover text-bone px-6 h-11">
              Contact support
            </Button>
          </Link>
        </div>

        <p className="mt-10 text-xs text-ink/50 leading-relaxed max-w-3xl" data-testid="pm-billing-disclaimer">
          This page is a design preview of the property-management billing dashboard. Plans, invoices and payment methods are illustrative only — no live subscriptions or payments are processed yet. Final pricing, taxes and regional adjustments will be published before general availability.
        </p>
      </main>

      <Footer />
    </div>
  );
}

function MiniStat({ icon: Icon, label, sub }) {
  return (
    <div className="flex items-center gap-2">
      <Icon size={14} className="text-bone/70" strokeWidth={1.5} />
      <div>
        <p className="text-sm font-medium leading-none">{label}</p>
        <p className="text-[10px] text-bone/60 mt-1 uppercase tracking-widest">{sub}</p>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-ink/60">{label}</dt>
      <dd className="text-ink font-medium">{value}</dd>
    </div>
  );
}
